import SessionGroupTile from './SessionGroupTile';
import DischargeTimerRow from '../cards/DischargeTimerRow';

const EYEBROW_TEXT = {
  combined_db_rcd: 'DISTRIBUTION BOARDS · TAP TO OPEN',
  combined_exit_emergency: 'EXIT & EMERGENCY · TAP TO OPEN',
  workshop_equipment: 'EQUIPMENT GROUPS · TAP TO OPEN',
};

/**
 * Session home screen — list of group tiles, one per board / group.
 *
 * Exit/emergency groups get a discharge timer under each tile. A 90-minute
 * discharge means starting several buildings and watching them run down together,
 * so the start control belongs here rather than only inside each group. It is the
 * same component and the same store state as the drill-down's block.
 *
 * The timer is a sibling of the tile, not a child: the tile is itself a <button>,
 * and a button cannot contain another button.
 *
 * Props:
 *   groups           — [{ groupName, groupCode, location, testedCount, totalCount }]
 *                      (pre-derived by MobileSessionView via deriveMobileGroupCardFields)
 *   sessionAssetType — used for the eyebrow label
 *   onTileClick      — (groupName: string) => void
 *   showTimers       — render a discharge timer per tile (exit/emergency sessions)
 *   getTimerStatus / getTimerRemainingMs — (groupName) => state, from the session store
 *   onTimerStart / onTimerRequestReset   — (groupName) => void
 */
export default function MobileGroupList({
  groups = [],
  sessionAssetType,
  onTileClick,
  showTimers = false,
  getTimerStatus,
  getTimerRemainingMs,
  onTimerStart,
  onTimerRequestReset,
}) {
  const eyebrow = EYEBROW_TEXT[sessionAssetType] || 'GROUPS · TAP TO OPEN';

  return (
    <div className="mobile-group-list">
      <p className="mobile-group-list__eyebrow">{eyebrow}</p>
      {groups.map(({ groupName, groupCode, location, testedCount, totalCount }) => {
        const tile = (
          <SessionGroupTile
            key={groupName}
            groupCode={groupCode}
            location={location}
            testedCount={testedCount}
            totalCount={totalCount}
            onClick={() => onTileClick(groupName)}
          />
        );

        // No timer: the tile stays a direct child of the list, as before.
        if (!showTimers) return tile;

        return (
          <div className="mobile-group-list__timed-tile" key={groupName}>
            {tile}
            <DischargeTimerRow
              groupCode={groupCode}
              status={getTimerStatus ? getTimerStatus(groupName) : 'idle'}
              remainingMs={getTimerRemainingMs ? getTimerRemainingMs(groupName) : 0}
              onStart={() => onTimerStart?.(groupName)}
              onRequestReset={() => onTimerRequestReset?.(groupName)}
            />
          </div>
        );
      })}
      {groups.length === 0 && (
        <p className="mobile-group-list__empty">No groups found for this session.</p>
      )}
    </div>
  );
}
